import { z } from "zod";

/**
 * Versioned tiling layout tree. `Split`s contain `TabGroup`s (or nested splits);
 * tab groups contain `Pane` leaves. The pane registry that resolves `paneType` to a
 * component is supplied by the app, not by this schema.
 */
export const LAYOUT_SCHEMA_VERSION = 1;

export const Pane = z.object({
  type: z.literal("pane"),
  id: z.string(),
  paneType: z.string(),
  params: z.record(z.string(), z.unknown()).default({}),
  title: z.string().optional(),
});
export type Pane = z.infer<typeof Pane>;

export const TabGroup = z.object({
  type: z.literal("tabs"),
  id: z.string(),
  tabs: z.array(Pane),
  activeTabId: z.string().optional(),
});
export type TabGroup = z.infer<typeof TabGroup>;

export type Split = {
  type: "split";
  id: string;
  direction: "horizontal" | "vertical";
  children: LayoutNode[];
  /** One ratio per child, summing to 1. */
  ratios: number[];
};
export type LayoutNode = Split | TabGroup;

export const Split: z.ZodType<Split> = z.object({
  type: z.literal("split"),
  id: z.string(),
  direction: z.enum(["horizontal", "vertical"]),
  get children() {
    return z.array(LayoutNode).min(1);
  },
  ratios: z.array(z.number().positive()).min(1),
});

export const LayoutNode: z.ZodType<LayoutNode> = z.union([Split, TabGroup]);

export const LayoutTree = z.object({
  version: z.literal(LAYOUT_SCHEMA_VERSION),
  root: LayoutNode,
  focusedPaneId: z.string().optional(),
});
export type LayoutTree = z.infer<typeof LayoutTree>;
